import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Calendar, 
  Edit, 
  FileText,
  Briefcase,
  Trash2,
  Building,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import type { Matter, MatterWithClient } from '../../types';

// Sort configuration types
type SortKey = 'title' | 'client' | 'status' | 'type' | 'lastActivity' | 'billedAmount' | 'dateCreated';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: SortKey | null;
  direction: SortDirection;
}

interface EnhancedMatterListProps {
  matters: MatterWithClient[];
  isLoading?: boolean;
  onMatterClick?: (matter: MatterWithClient) => void;
  onEditMatter?: (matter: MatterWithClient) => void;
  onDeleteMatter?: (matter: MatterWithClient) => void;
  onClientClick?: (clientId: string) => void;
  showClientColumn?: boolean;
  className?: string;
}

const EnhancedMatterList: React.FC<EnhancedMatterListProps> = ({
  matters,
  isLoading = false,
  onMatterClick,
  onEditMatter,
  onDeleteMatter,
  onClientClick,
  showClientColumn = true,
  className = ''
}) => {
  const navigate = useNavigate();
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc'
  });

  // Handle sorting
  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort matters based on current sort configuration
  const sortedMatters = useMemo(() => {
    if (!sortConfig.key) return matters;

    return [...matters].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'client':
          aValue = a.client.name.toLowerCase();
          bValue = b.client.name.toLowerCase();
          break;
        case 'status':
          // Define status order for sorting
          const statusOrder = { 'active': 1, 'on-hold': 2, 'completed': 3, 'cancelled': 4 };
          aValue = statusOrder[a.status] || 5;
          bValue = statusOrder[b.status] || 5;
          break;
        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          break;
        case 'lastActivity':
          aValue = new Date(a.lastActivity).getTime();
          bValue = new Date(b.lastActivity).getTime();
          break;
        case 'billedAmount':
          aValue = a.billedAmount;
          bValue = b.billedAmount;
          break;
        case 'dateCreated':
          aValue = new Date(a.dateCreated).getTime();
          bValue = new Date(b.dateCreated).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [matters, sortConfig]);

  // Get sort indicator icon
  const getSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  // Get status badge styling and icon
  const getStatusBadge = (status: Matter['status']) => {
    switch (status) {
      case 'active':
        return {
          className: 'bg-green-900 text-green-300 border border-green-700',
          icon: <Clock className="w-3 h-3" />,
          label: 'Active'
        };
      case 'completed':
        return {
          className: 'bg-blue-900 text-blue-300 border border-blue-700',
          icon: <CheckCircle className="w-3 h-3" />,
          label: 'Completed'
        };
      case 'on-hold':
        return {
          className: 'bg-yellow-900 text-yellow-300 border border-yellow-700',
          icon: <AlertCircle className="w-3 h-3" />,
          label: 'On Hold'
        };
      case 'cancelled':
        return {
          className: 'bg-red-900 text-red-300 border border-red-700',
          icon: <XCircle className="w-3 h-3" />,
          label: 'Cancelled'
        };
      default:
        return {
          className: 'bg-gray-700 text-gray-300 border border-gray-600',
          icon: <Clock className="w-3 h-3" />,
          label: 'Unknown'
        };
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle matter click
  const handleMatterClick = (matter: MatterWithClient) => {
    if (onMatterClick) {
      onMatterClick(matter);
    }
  };

  // Handle client click
  const handleClientClick = (e: React.MouseEvent, clientId: string) => {
    e.stopPropagation(); // Prevent matter click
    if (onClientClick) {
      onClientClick(clientId);
    } else {
      // Default navigation to client details
      navigate(`/clients/${clientId}`);
    }
  };

  // Handle edit click
  const handleEditClick = (e: React.MouseEvent, matter: MatterWithClient) => {
    e.stopPropagation(); // Prevent matter click
    if (onEditMatter) {
      onEditMatter(matter);
    }
  };

  // Handle delete click
  const handleDeleteClick = (e: React.MouseEvent, matter: MatterWithClient) => {
    e.stopPropagation(); // Prevent matter click
    if (onDeleteMatter) {
      onDeleteMatter(matter);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-700 ${className}`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading matters...</p>
        </div>
      </div>
    );
  }

  if (matters.length === 0) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-700 ${className}`}>
        <div className="p-8 text-center">
          <Briefcase className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No matters found</h3>
          <p className="text-gray-500">There are no matters to display yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-800 rounded-lg border border-gray-700 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 bg-gray-750">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Briefcase className="w-5 h-5" />
          Matters ({sortedMatters.length})
        </h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-750">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center justify-between">
                  <span>Matter</span>
                  {getSortIcon('title')}
                </div>
              </th>
              {showClientColumn && (
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                  onClick={() => handleSort('client')}
                >
                  <div className="flex items-center justify-between">
                    <span>Client</span>
                    {getSortIcon('client')}
                  </div>
                </th>
              )}
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center justify-between">
                  <span>Status</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                onClick={() => handleSort('type')}
              >
                <div className="flex items-center justify-between">
                  <span>Type</span>
                  {getSortIcon('type')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                onClick={() => handleSort('lastActivity')}
              >
                <div className="flex items-center justify-between">
                  <span>Last Activity</span>
                  {getSortIcon('lastActivity')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition-colors select-none"
                onClick={() => handleSort('billedAmount')}
              >
                <div className="flex items-center justify-between">
                  <span>Billed</span>
                  {getSortIcon('billedAmount')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {sortedMatters.map((matter) => {
              const statusBadge = getStatusBadge(matter.status);
              
              return (
                <tr 
                  key={matter.id}
                  className="hover:bg-gray-750 cursor-pointer transition-colors duration-150"
                  onClick={() => handleMatterClick(matter)}
                >
                  {/* Matter Info */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-400 mt-0.5" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-white">
                          {matter.title}
                        </div>
                        {matter.description && (
                          <div className="text-sm text-gray-400 max-w-xs truncate">
                            {matter.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Client Info */}
                  {showClientColumn && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div 
                        className="flex items-center cursor-pointer hover:text-blue-400 transition-colors"
                        onClick={(e) => handleClientClick(e, matter.clientId)}
                      >
                        <Building className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-white">
                            {matter.client.name}
                          </div>
                          {matter.client.contactName && (
                            <div className="text-xs text-gray-400">
                              {matter.client.contactName}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  )}

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
                      {statusBadge.icon}
                      {statusBadge.label}
                    </span>
                  </td>

                  {/* Type */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      {matter.type}
                    </div>
                  </td>

                  {/* Last Activity */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-400">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(matter.lastActivity)}
                    </div>
                  </td>

                  {/* Billed Amount */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-green-400">
                      {formatCurrency(matter.billedAmount)}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {onEditMatter && (
                        <button
                          onClick={(e) => handleEditClick(e, matter)}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          title="Edit matter"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {onDeleteMatter && (
                        <button
                          onClick={(e) => handleDeleteClick(e, matter)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete matter"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnhancedMatterList;
